/* eslint-disable no-param-reassign, max-lines-per-function, max-lines */
import { createStore } from "vuex";
import omit from "lodash.omit";
import { pluralize } from "common/utils/format/word";
import { api } from "jiffy-api";
import { mergeDeep } from "common/utils/core";
import { PlainError } from "common/utils/errors";
import { isMobile } from "common/utils/browser";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { track, registerMixpanel } from "jiffy-analytics";
import { storage } from "common/utils/storage";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { EventBus, Events } from "jiffy-store";
import { clearCachedEstimates } from "components/jiffy/product-details/cache-composition-delivery-date";
import scrollToAlerts, { scrollToTop } from "./helpers/sroll-to-alerts";
import plugins from "./plugins";
import dtfBogoModule from "./modules/dtf-bogo";
import paymentsModule from "./modules/payments";
import savedCartsModule from "./modules/saved-carts";
import shippingModule from "./modules/shipping";

const $getCsrfMeta = () => document.querySelector('meta[name="csrf-token"]');
const obtainCsrfToken = () => $getCsrfMeta()?.getAttribute("content");

const getCurrentRelativePath = () => {
  if (typeof window === "undefined") {
    return "/";
  }

  const { pathname = "/", search = "", hash = "" } = window.location;

  return `${pathname}${search}${hash}`;
};

const initialState = {
  cartConfig: {
    orderNumber: null,
    storeCode: "us",
    isMcuCart: false,
    isCrafterOnboarded: false,
    productSuggestions: false,
    j1Cart: false,
    checkoutLoading: false,
    inventoryLoading: false,
    couponLoading: false,
    locationLoading: false,
    removingItem: false,
    refreshLoading: false,
    isIncorrectAddress: false,
    isAccountPresent: false,
    isStandardDeliveryInfoVisible: false,
    customer: {},
    guestZipcode: "",
    autoCartType: null,
    signInURL: null, // Will be computed dynamically via getter
    createAccountURL: null, // Will be computed dynamically via getter
    fgWithNonFastestOption: ["4"], // [Port & Company]
    csrfToken: obtainCsrfToken(),
    dtfPromotionBlockVisible: false,
    dtfCouponApplied: false,
    madeShippingRule: null,
    dtfShippingRule: null,
    imageServiceShippingRule: null,
    spangleShippingRule: null,
    showCouponBoxOnBogo: true
  },
  cartHeader: {
    clearButtonInformation: {}
  },
  accountAddresses: {
    addresses: null,
    shipAddressId: null
  },
  experiments: {},
  itemsOutOfStock: {},
  couponCode: {
    appliedCouponCode: "",
    couponDiscount: 0.0,
    freeShipping: false
  },
  orderGroups: {},
  subOrdersSummary: {},
  subOrdersHasJiffyMade: false,
  notifications: [],
  summary: {
    isRetailPriceCorrect: true,
    retailSavings: 0.0,
    productsSubtotal: 0.0,
    automaticPromotionDiscount: 0.0,
    totalItems: 0,
    total: 0.0,
    taxTotal: 0.0,
    retailTotal: 0.0,
    shippingUrl: "",
    bulkDiscount: 0.0,
    saleDiscount: 0.0,
    couponDiscount: 0.0,
    currencyCode: "USD",
    countryCode: "US",
    groups: []
  },
  zipcode: {},
  storeCredit: {
    amount: 0.0,
    isVisible: false,
    isUsed: false,
    closestToExpire: {}
  },
  specificStoreCredits: {},
  location: {},
  locationAddresses: [],
  matchingProduct: null,
  browseCustomShirtsLineItemId: null,
  modalState: {
    shownZipcodeModal: false,
    showStickyCheckout: false
  },
  etsy: {
    observationPeriod: null,
    platformName: null,
    unmappedEtsyLineItemsByListing: []
  },
  platform: {
    appPlatform: window.MOBILE_APP_PLATFORM,
    env: window.jiffy?.env,
    host: window.jiffy?.host,
    isApp: window.MOBILE_APP,
    isMobile: isMobile()
  },
  usp: {
    bulkDiscountAt: "$99",
    dtfFreeShippingThreshold: "29",
    freeShippingAt: "$59",
    freeReturnsFor: "100"
  },
  dtf: {
    isRetrying: false,
    showModal: false,
    actionCableConnected: true
  },
  dtfPricing: {},
  assignmentDuration: null,
  preCutConfig: {},
  reviewsData: {
    sticker: {
      avgRating: null,
      reviewsCount: null
    }
  },
  variantsData: [],
  storeCreditsByBusinessLines: false,
  isEligibleForBogoPromotion: false,
  maxFreeDimensionLabel: "12x12",
  expectedTransfersStoreCredit: null,
  fullyPayingWithStoreCredits: false,
  cartSubtotalAfterStoreCredits: 0,
  deliveryLoadingCounter: 0,
  removeItemQueue: {
    isProcessing: false,
    queue: {}
  },
  llmRedrawRequests: {
    // Map of artworkId to request data
    byArtworkId: {},
    isLoading: false, // Keep for backward compatibility
    // Track loading state per artwork ID
    loadingByArtworkId: {}
  }
};

const store = createStore({
  state() {
    return {
      cartState: { ...initialState }
    };
  },
  mutations: {
    updateCartState(state, { cartData, isInitialization = false }) {
      const excludeKeys = [
        ...Object.keys(paymentsModule.state()),
        ...Object.keys(shippingModule.state()),
        ...Object.keys(dtfBogoModule.state())
      ];
      const excludeConfigKeys = [...Object.keys(savedCartsModule.state())];

      if (isInitialization) {
        // Deep merge for initialization
        state.cartState = mergeDeep(state.cartState, objNamesToCamelCase(cartData), excludeKeys);
        state.cartState.cartConfig = omit(state.cartState.cartConfig, excludeConfigKeys);
      } else {
        // Shallow merge with specific deep merging for replacement
        const newState = omit(objNamesToCamelCase(cartData), excludeKeys);

        state.cartState = {
          ...state.cartState,
          ...newState,
          cartConfig: {
            ...state.cartState.cartConfig,
            ...omit(newState.cartConfig, excludeConfigKeys)
          },
          summary: {
            ...state.cartState.summary,
            ...newState.summary
          }
        };
      }
    },
    pushNotification(state, notification) {
      state.cartState.notifications = [...state.cartState.notifications, notification];
    },
    saveConfigCitySelected(state, status) {
      state.cartState.cartConfig.citySelected = status;
    },
    removeNotification(state, id) {
      state.cartState.notifications = state.cartState.notifications.filter(item => item.id !== id);
    },
    setCheckoutLoadingStatus(state, status) {
      state.cartState.cartConfig.checkoutLoading = status;
    },
    setInventoryLoadingStatus(state, status) {
      state.cartState.cartConfig.inventoryLoading = status;
    },
    setCouponLoadingStatus(state, status) {
      state.cartState.cartConfig.couponLoading = status;
    },
    setRemovingItem(state, itemId) {
      state.cartState.cartConfig.removingItem = itemId;
    },
    setSystemError(state, text = "System error. Please try again later.") {
      state.cartState.cartConfig.checkoutLoading = false;
      state.cartState.cartConfig.couponLoading = false;

      if (text) {
        state.cartState.notifications = [
          {
            type: "error",
            text
          }
        ];

        window.Honeybadger?.notify("Vue Cart System Error", {
          params: {
            error_message: text,
            cart_order_number: state.cartState.cartConfig.orderNumber,
            cart_total_items: state.cartState.summary.totalItems,
            cart_total: state.cartState.summary.total,
            user_id: state.cartState.cartHeader.clearButtonInformation.userId,
            is_mcu_cart: state.cartState.cartConfig.isMcuCart,
            store_code: state.cartState.cartConfig.storeCode,
            stack_trace: new Error().stack
          }
        });
      }
    },
    setCurrentNotification(state, notification) {
      state.cartState.notifications = [notification];
    },
    getCurrentLocationSuccess(state, location) {
      state.cartState.location = location;
    },
    changeAddressSuccess(state, id) {
      state.cartState.accountAddresses.shipAddressId = id;
    },
    setLocationLoadingStatus(state, status) {
      state.cartState.cartConfig.locationLoading = status;
    },
    setRefreshLoading(state, status) {
      state.cartState.cartConfig.refreshLoading = status;
    },
    setIsCrafterOnboarded(state, isOnboarded) {
      state.cartState.cartConfig.isCrafterOnboarded = isOnboarded;
    },
    setOrderNumber(state, number) {
      state.cartState.cartConfig.orderNumber = number;
    },
    setGuestZipcode(state, zipcode) {
      state.cartState.cartConfig.guestZipcode = zipcode;
    },
    setUnmappedEtsyLineItemsByListing(state, itemsByVariant) {
      state.cartState.etsy.unmappedEtsyLineItemsByListing = itemsByVariant;
    },
    setPlatformName(state, platformName) {
      state.cartState.etsy.platformName = platformName;
    },
    setEtsyObservationPeriod(state, period) {
      state.cartState.etsy.observationPeriod = period;
    },
    setVariantsData(state, variants) {
      state.cartState.variantsData = variants;
    },
    setProductData(state, product) {
      state.cartState.productData = product;
    },
    toggleZipcodeModalVisibility(state, callOriginId) {
      state.cartState.modalState.shownZipcodeModal = !state.cartState.modalState.shownZipcodeModal;

      if (callOriginId) {
        state.cartState.modalState.callOriginId = callOriginId;
      }
    },
    toggleCheckoutModalVisibility(state, show) {
      state.cartState.modalState.showStickyCheckout =
        show === undefined ? !state.cartState.modalState.showStickyCheckout : show;
    },
    updateCouponDiscount(state, discount) {
      state.couponCode.couponDiscount = discount;
    },
    updateStoreCreditUsageState(state, value) {
      state.cartState.storeCredit.isUsed = value;
    },
    updateSpecificStoreCreditUsageState(state, { businessLine, isUsed }) {
      state.cartState.specificStoreCredits[businessLine] ||= {};
      state.cartState.specificStoreCredits[businessLine].isUsed = isUsed;
    },
    setAssignmentDuration(state, value) {
      state.cartState.assignmentDuration = value;
    },
    setAddedArtworkSize(state, id) {
      state.cartState.addedArtworkSize = id;
    },
    // We have 2 vuex store that uses DtfProgressBar, this mutation is used on
    // the other store but not here. Mocking it to avoid errors.
    updateArtworkValues(_state, _value) {},
    setRemoveItemProcessing(state, isProcessing) {
      state.cartState.removeItemQueue.isProcessing = isProcessing;
    },
    addToRemoveItemQueue(state, payload) {
      const key = `${payload.line_item_id}_${payload.quantity || 0}`;
      state.cartState.removeItemQueue.queue[key] = payload;
    },
    clearRemoveItemQueue(state) {
      state.cartState.removeItemQueue.queue = {};
    },
    removeFromQueue(state, payload) {
      const key = `${payload.line_item_id}_${payload.quantity || 0}`;
      delete state.cartState.removeItemQueue.queue[key];
    },
    setLlmRedrawLoading(state, { artworkId, isLoading }) {
      // Support both old (global) and new (per-artwork) patterns
      if (typeof artworkId === "boolean") {
        // Legacy support: if called with just boolean, it's the old pattern
        state.cartState.llmRedrawRequests.isLoading = artworkId;
      } else if (isLoading) {
        // New pattern: set loading state per artwork ID
        state.cartState.llmRedrawRequests.loadingByArtworkId = {
          ...state.cartState.llmRedrawRequests.loadingByArtworkId,
          [artworkId]: true
        };
      } else {
        // Remove loading state when done
        const { [artworkId]: removed, ...rest } =
          state.cartState.llmRedrawRequests.loadingByArtworkId;
        state.cartState.llmRedrawRequests.loadingByArtworkId = rest;
      }
    },
    setLlmRedrawRequest(state, { artworkId, data }) {
      // Use Vue.set for reactivity in Vue 2
      const existingData = state.cartState.llmRedrawRequests.byArtworkId[artworkId] || {};
      const updatedData = {
        ...existingData,
        ...data
      };
      // Using object spread to trigger reactivity
      state.cartState.llmRedrawRequests.byArtworkId = {
        ...state.cartState.llmRedrawRequests.byArtworkId,
        [artworkId]: updatedData
      };
    },
    clearLlmRedrawRequest(state, artworkId) {
      // Create new object without the artworkId for reactivity
      const { [artworkId]: removed, ...rest } = state.cartState.llmRedrawRequests.byArtworkId;
      state.cartState.llmRedrawRequests.byArtworkId = rest;
    },
    updateLlmRedrawRequestState(state, { artworkId, currentState }) {
      const existingData = state.cartState.llmRedrawRequests.byArtworkId[artworkId];
      if (existingData) {
        state.cartState.llmRedrawRequests.byArtworkId = {
          ...state.cartState.llmRedrawRequests.byArtworkId,
          [artworkId]: {
            ...existingData,
            currentState
          }
        };
      }
    },
    setLlmRedrawQuestions(state, { artworkId, questions }) {
      const existingData = state.cartState.llmRedrawRequests.byArtworkId[artworkId];
      if (existingData) {
        state.cartState.llmRedrawRequests.byArtworkId = {
          ...state.cartState.llmRedrawRequests.byArtworkId,
          [artworkId]: {
            ...existingData,
            questions
          }
        };
      }
    },
    updateLineItemEnhancementNote(state, { artworkId, enhancementNote }) {
      // Find and update the line item with the new enhancement note
      const { groups } = state.cartState.orderGroups;
      if (!groups) return;

      Object.values(groups).forEach(group => {
        const { lineItems } = group;
        Object.values(lineItems).forEach(lineItem => {
          // Check main line item
          if (lineItem.artworkId === artworkId) {
            lineItem.artworkNote = enhancementNote;
          }
          // Also check madeArtworkNotes array
          if (lineItem.madeArtworkNotes && Array.isArray(lineItem.madeArtworkNotes)) {
            lineItem.madeArtworkNotes.forEach(madeNote => {
              if (madeNote.artworkId === artworkId) {
                madeNote.artworkNote = enhancementNote;
                madeNote.artworkNoteHtml = undefined;
              }
            });
          }
        });
      });
    },

    setMatchingProduct(state, lineItem) {
      state.cartState.matchingProduct = lineItem;
    },
    setBrowseCustomShirtsLineItemId(state, lineItemId) {
      state.cartState.browseCustomShirtsLineItemId = lineItemId;
    }
  },
  getters: {
    // # Config
    isPrintOnlyCart: state => state.cartState.cartConfig.isPrintOnlyCart,
    csrfToken: state => state.cartState.cartConfig.csrfToken,
    fgWithNonFastestOption: state => state.cartState.cartConfig.fgWithNonFastestOption,
    createAccountURL: _state =>
      `/signup?redirect_to=${encodeURIComponent(getCurrentRelativePath())}`,
    signInURL: _state => `/login?redirect_to=${encodeURIComponent(getCurrentRelativePath())}`,
    currentLocationLoading: state => state.cartState.cartConfig.locationLoading,
    storeCode: state => state.cartState.cartConfig.storeCode,
    isUs: state => state.cartState.cartConfig.storeCode === "us",
    storeCurrency: (state, getters) => (getters.isUs ? "$" : "CA$"),
    accountPresent: state => state.cartState.cartConfig.isAccountPresent,
    cartBulkDiscount: state => parseFloat(state.cartState.summary.bulkDiscount),
    automaticPromotionDiscount: state =>
      parseFloat(state.cartState.summary.automaticPromotionDiscount || 0),
    initCitySelected: state => state.cartState.cartConfig.citySelected,
    isMcuCart: state => state.cartState.cartConfig.isMcuCart,
    checkoutLoading: state => state.cartState.cartConfig.checkoutLoading,
    inventoryLoading: state => state.cartState.cartConfig.inventoryLoading,
    j1Cart: state => state.cartState.cartConfig.j1Cart,
    couponLoading: state => state.cartState.cartConfig.couponLoading,
    refreshLoading: state => state.cartState.cartConfig.refreshLoading,
    removingItem: state => state.cartState.cartConfig.removingItem,
    assignmentDuration: state => state.cartState.assignmentDuration,
    dtfPromotionBlockVisible: _state => !storage.isExists("__JIFFY__DTF_CTA_CART_CLOSED__"),
    dtfCouponApplied: state => state.cartState.cartConfig.dtfCouponApplied,
    madeShippingRule: state => state.cartState.cartConfig.madeShippingRule,
    dtfShippingRule: state => state.cartState.cartConfig.dtfShippingRule,
    imageServiceShippingRule: state => state.cartState.cartConfig.imageServiceShippingRule,
    spangleShippingRule: state => state.cartState.cartConfig.spangleShippingRule,
    dtfPricing: state => catalogNumber => state.cartState.dtfPricing[catalogNumber],
    enableSmartRedrawNotes: () => false,
    showCouponBoxOnBogo: state => state.cartState.cartConfig?.showCouponBoxOnBogo === true,
    madeSamplesV1: state => state.cartState.cartConfig?.madeSamplesV1 === true,
    showStickyApplePayButton: state =>
      state.cartState.cartConfig?.showStickyApplePayButton === true,
    isRichUIForSavedCarts: state => !state.cartState.platform.isMobile,
    isIncorrectAddress: (state, getters) => {
      // backend validation
      if (state.cartState.cartConfig.isIncorrectAddress) {
        return true;
      }

      // auth user: without saved address and without entered zip
      if (getters.accountPresent && !getters.addressPresent && !getters.citySelected) {
        return true;
      }

      // guest user: without entered zip
      if (!getters.accountPresent && !getters.citySelected) {
        return true;
      }

      // state without delivery
      if (getters["shipping/invalidDeliveryState"]) {
        return true;
      }

      return false;
    },
    orderNumber: state => state.cartState.cartConfig.orderNumber,
    userId: state => state.cartState.cartHeader.clearButtonInformation.userId,
    isEmpty: state => state.cartState.summary.totalItems === 0,
    modalState: state => state.cartState.modalState,
    experiments: state => state.cartState.experiments,
    maxFreeDimensionLabel: state => state.cartState.maxFreeDimensionLabel || "12x12",
    matchingProduct: state => state.cartState.matchingProduct,
    browseCustomShirtsLineItemId: state => state.cartState.browseCustomShirtsLineItemId,

    // # Addresses
    addressesUrl: state => state.cartState.accountAddresses.addressesUrl,
    shipAddressId: state => state.cartState.accountAddresses.shipAddressId,
    addresses: state => state.cartState.accountAddresses.addresses,
    addressPresent: state => !!state.cartState.accountAddresses.addresses.length,
    completeAddressesPresent: state => state.cartState.accountAddresses.hasCompleteAddresses,
    addressSelected: (state, { addresses = [], shipAddressId }) =>
      addresses.find(({ id }) => id === shipAddressId),

    // # Coupon Code
    couponCode: state => state.cartState.couponCode,
    hasCouponApplied: state =>
      parseFloat(state.cartState.summary.couponDiscount) > 0 ||
      parseFloat(state.cartState.couponCode.couponDiscount) > 0 ||
      state.cartState.couponCode.appliedCouponCode ||
      state.cartState.couponCode.freeShipping,

    // # Location
    location: state => state.cartState.location,
    locationAddresses: state => state.cartState.locationAddresses,
    locationLoaded: state => Object.keys(state.cartState.location).length > 0,
    citySelected: state =>
      state.cartState.location.citySelected || state.cartState.location.cityDefault,
    cityDefault: state => state.cartState.location.cityDefault,

    // # Order Groups
    orderGroups: state => state.cartState.orderGroups,

    // # Store Credit
    isStoreCreditVisible: state =>
      state.cartState.storeCredit.isVisible && parseFloat(state.cartState.storeCredit.amount) > 0,
    storeCredit: state => state.cartState.storeCredit,
    isSpecificStoreCreditVisible: (state, getters) => businessLine =>
      businessLine === "all"
        ? getters.isStoreCreditVisible
        : state.cartState.specificStoreCredits[businessLine]?.isVisible &&
          parseFloat(state.cartState.specificStoreCredits[businessLine]?.amount || "0.0") > 0,
    specificStoreCredits: state => state.cartState.specificStoreCredits,
    useSpecificStoreCredits: (_state, getters) =>
      Object.fromEntries(
        Object.entries(getters.specificStoreCredits).map(([key, { isUsed }]) => [key, isUsed])
      ),
    expectedTransfersStoreCredit: state => state.cartState.expectedTransfersStoreCredit,
    fullyPayingWithStoreCredits: state => state.cartState.fullyPayingWithStoreCredits,
    transfersCartSubtotal: state => state.cartState.transfersCartSubtotal,
    transfersStoreCreditTiers: state => state.cartState.transfersStoreCreditTiers,

    // # Summary
    cartSummary: state => state.cartState.summary,
    subOrderSummary: state => id => state.cartState.subOrdersSummary[id],
    productsSubtotal: state => parseFloat(state.cartState.summary.productsSubtotal),
    deliveryTotal: (_state, getters) => getters["shipping/deliveryTotal"],
    surchargeTotal: (_state, getters) => getters["shipping/surchargeTotal"],
    cartTotal: (state, getters) =>
      parseFloat(state.cartState.summary.total) +
      getters.shippingTotal +
      getters.taxTotal +
      getters.processingFee,
    netTotal: (_, getters) => {
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;
      let grandTotal = getters.cartTotal;
      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) grandTotal -= parseFloat(amount);

        return grandTotal === 0;
      });
      if (grandTotal > 0 && useStoreCredits) {
        grandTotal -= parseFloat(storeCreditAmount);
      }
      return Math.max(grandTotal, 0);
    },
    cartSubtotalAfterStoreCredits: (state, getters) => {
      const baseSubtotal = parseFloat(state.cartState.cartSubtotalAfterStoreCredits || 0);
      const automaticPromotionShippingDiscount =
        getters["shipping/automaticPromotionShippingDiscount"] || 0;
      return Math.max(baseSubtotal - automaticPromotionShippingDiscount, 0);
    },
    taxTotal: state => parseFloat(state.cartState.summary.taxTotal),
    processingFee: (state, getters) => {
      const fee = parseFloat(state.cartState.summary.processingFeeAmount) || 0;
      const total = parseFloat(state.cartState.summary.total) + getters.shippingTotal;
      if (total >= parseFloat(state.cartState.summary.processingFeeThreshold)) {
        return 0;
      }

      return fee;
    },
    shippingTotal: (_state, getters) => getters["shipping/shippingTotal"],
    // # Used by payment method subtypes, i.e. PayPal, Apple Pay, Google Pay, etc.
    currencyCode: state => state.cartState.summary.currencyCode,
    countryCode: state => state.cartState.summary.countryCode,
    // # Out Of Stock
    itemsOutOfStock: state => state.cartState.itemsOutOfStock,
    hasOutOfStock: state => Object.keys(state.cartState.itemsOutOfStock).length > 0,
    itemsTotalOutOfStock: state =>
      Object.values(state.cartState.itemsOutOfStock).reduce((sum, item) => sum + item.missing, 0),
    // # Consolidation
    isParent: state => state.cartState.baseOrders.length > 0,
    childCartsCount: state => state.cartState.baseOrders.length,
    childCartsText: state => pluralize(state.cartState.baseOrders.length, "Cart"),

    // # Unsorted
    hasJiffyMade: state => state.cartState.subOrdersHasJiffyMade,
    allLineItems: state => {
      const { groups } = state.cartState.orderGroups;
      if (!groups) return [];

      const items = [];
      Object.values(groups).forEach(group => {
        const { lineItems } = group;
        Object.values(lineItems).forEach(lineItem => {
          items.push(lineItem);
        });
      });
      return items;
    },
    cartBusinessLines: (state, getters) => {
      const businessLines = new Set();

      getters.allLineItems.forEach(item => {
        if (item.businessLine === "Blanks") businessLines.add("Blanks");
        if (item.businessLine === "DTF") businessLines.add("Transfers");
      });

      return Array.from(businessLines);
    },
    allItemsAreMadeOrder: (state, getters) => {
      const { allLineItems } = getters;
      if (allLineItems.length === 0) return false;

      let hasPrintItems = false;
      let allItemsAreMadeRelated = true;

      for (const lineItem of allLineItems) {
        const { businessLine } = lineItem;

        if (businessLine === "Print") {
          hasPrintItems = true;
        } else if (businessLine !== "AI") {
          allItemsAreMadeRelated = false;
          break;
        }
      }

      return hasPrintItems && allItemsAreMadeRelated;
    },
    deliveryNotification: (state, getters) => getters["shipping/deliveryNotification"],
    zipcodeState: state => state.cartState.zipcode,
    isCartDisabled: (state, getters) => getters.isIncorrectAddress || getters.hasOutOfStock,
    showNewCheckoutError: state => state.cartState.showNewCheckoutError,
    showProductSuggestions: state => state.cartState.productSuggestions,
    guestZipcode: state => state.cartState.cartConfig.guestZipcode,
    isEtsyAutoCart: state => state.cartState.cartConfig.autoCartType !== null,
    showEtsyUnmappedItems: (state, getters) =>
      getters.isEtsyAutoCart && state.cartState.etsy.unmappedEtsyLineItemsByListing?.length,
    unmappedEtsyLineItemsByListing: state => state.cartState.etsy.unmappedEtsyLineItemsByListing,
    etsyObservationPeriod: state => state.cartState.etsy.observationPeriod,
    platformName: state => state.cartState.etsy.platformName,
    appPlatform: state => state.cartState.platform.appPlatform,
    host: state => state.cartState.platform.host,
    env: state => state.cartState.platform.env,
    isApp: state => state.cartState.platform.isApp,
    isMobile: state => state.cartState.platform.isMobile,
    uspData: state => state.cartState.usp,
    isAndroidApp: state => state.cartState.platform.appPlatform === "android",
    // We have 2 vuex store that uses DtfProgressBar. showModal, isRetrying and actionCableConnected is used
    // on the other store but not here. Mocking it to avoid errors.
    showModal: state => state.cartState.dtf.showModal,
    isRetrying: state => state.cartState.dtf.isRetrying,
    actionCableConnected: state => state.cartState.dtf.actionCableConnected,
    preCutConfig: state => state.cartState.preCutConfig,
    reviewsData: state => state.cartState.reviewsData,
    variantsData: state => state.cartState.variantsData,
    productData: state => state.cartState.productData,
    storeCreditsByBusinessLines: state => state.cartState.storeCreditsByBusinessLines,
    showTransfersStoreCreditBanner: state => state.cartState.showTransfersStoreCreditBanner,
    addedArtworkSize: state => state.cartState.addedArtworkSize,
    removeItemQueueProcessing: state => state.cartState.removeItemQueue.isProcessing,
    removeItemQueue: state => state.cartState.removeItemQueue.queue,
    hasQueuedRemovals: state => Object.keys(state.cartState.removeItemQueue.queue).length > 0,
    isItemBeingRemoved: state => lineItemId => {
      // Check if currently being removed
      if (state.cartState.cartConfig.removingItem === lineItemId) {
        return true;
      }
      // Check if in queue
      const queueKeys = Object.keys(state.cartState.removeItemQueue.queue);
      return queueKeys.some(key => key.startsWith(`${lineItemId}_`));
    },

    // LLM Redraw Request getters
    llmRedrawRequestsLoading: state => state.cartState.llmRedrawRequests.isLoading,
    llmRedrawRequestLoadingByArtworkId: state => artworkId =>
      state.cartState.llmRedrawRequests.loadingByArtworkId[artworkId] || false,
    llmRedrawRequestByArtworkId: state => artworkId =>
      state.cartState.llmRedrawRequests.byArtworkId[artworkId] || null,
    llmRedrawRequestState: state => artworkId => {
      const request = state.cartState.llmRedrawRequests.byArtworkId[artworkId];
      return request?.currentState || "initial";
    },
    llmRedrawRequestQuestions: state => artworkId => {
      const request = state.cartState.llmRedrawRequests.byArtworkId[artworkId];
      return request?.questions || [];
    },
    llmRedrawRequestId: state => artworkId => {
      const request = state.cartState.llmRedrawRequests.byArtworkId[artworkId];
      return request?.llmRedrawRequestId || null;
    }
  },
  // Actions contains all asynchronous operations as ajax calls or Promises.
  actions: {
    async initializeStore({ commit, dispatch }, initialCart) {
      commit("shipping/setDeliveryLoadingStatus", false);

      // First, set the initial cart response
      dispatch("setCartResponse", {
        ...initialCart,
        skipLoadingUpdate: true,
        isInitialization: true
      });

      // Then conditionally get delivery dates if location/address data is available
      await dispatch("conditionallyFetchInitialDeliveryDates", initialCart);
    },

    async conditionallyFetchInitialDeliveryDates({ dispatch }, initialCart) {
      const hasLocation =
        initialCart.cart_config?.current_location?.city_selected ||
        initialCart.cart_config?.current_location?.city_default;
      const hasAddress = initialCart.account_addresses?.addresses?.length > 0;

      if (hasAddress || hasLocation) {
        await dispatch("shipping/getInitialDeliveryDates");
      }
    },
    prepareToAjax({ commit }) {
      commit("shipping/setInitialDeliveryDateMixpanelData", {
        earliestTimeToGetDate: 0,
        cartTimeToGetDate: 0
      });
      commit("shipping/setChangeDeliveryDateMixpanelData", {
        earliestTimeToGetDate: 0,
        cartTimeToGetDate: 0
      });
      commit("setCouponLoadingStatus", true);
      commit("shipping/setDeliveryLoadingStatus", true);
    },
    setCartResponse(
      { dispatch, commit, state },
      { skipLoadingUpdate = false, isInitialization = false, ...payload }
    ) {
      if (!isInitialization) {
        scrollToAlerts(state.cartState.notifications, objNamesToCamelCase(payload.notifications));
      }
      if (!skipLoadingUpdate) {
        commit("setCouponLoadingStatus", false);
        commit("shipping/setDeliveryLoadingStatus", false);
      }
      dispatch("replaceCartState", { ...payload, isInitialization });
    },
    replaceCartState({ commit }, { isInitialization = false, ...payload }) {
      commit("updateCartState", { cartData: payload, isInitialization });
      commit("payments/init", payload);
      commit("shipping/init", payload);
      commit("savedCarts/init", payload);
    },
    /**
     * Remove line item action with queue management to prevent concurrent requests.
     *
     * Logic:
     * 1. First click - makes API call using updateLineItems
     * 2. Subsequent clicks while processing - adds to queue
     * 3. After each completion - processes next batch from queue
     *
     * This prevents server-side errors from multiple concurrent remove requests.
     * Always uses updateLineItems API for consistency.
     *
     * Usage in components:
     * - Use `isItemBeingRemoved(lineItemId)` getter to show loading state
     * - Use `removeItemQueueProcessing` getter to check if any removal is in progress
     */
    removeLineItem({ dispatch, commit, getters }, payload) {
      // If already processing, add to queue
      if (getters.removeItemQueueProcessing) {
        commit("addToRemoveItemQueue", payload);
        return Promise.resolve();
      }

      // Start processing
      commit("setRemovingItem", payload.line_item_id);
      commit("setRemoveItemProcessing", true);

      return dispatch("processRemoveItems", [payload])
        .catch(() => {
          commit("setRemoveItemProcessing", false);
        })
        .finally(() => {
          commit("setRemovingItem", null);
          dispatch("processRemoveItemQueue");
        });
    },

    removeLineItems({ dispatch, commit, getters }, items) {
      if (getters.removeItemQueueProcessing) {
        items.forEach(item => commit("addToRemoveItemQueue", item));
        return Promise.resolve();
      }

      // Start processing
      commit("setRemovingItem", items[0].line_item_id);
      commit("setRemoveItemProcessing", true);

      return dispatch("processRemoveItems", items)
        .catch(() => {
          commit("setRemovingItem", null);
          commit("setRemoveItemProcessing", false);
        })
        .finally(() =>
          // Process any queued items
          dispatch("processRemoveItemQueue")
        );
    },

    processRemoveItems({ commit, dispatch }, queuedItems) {
      dispatch("prepareToAjax");
      const payload = {
        line_items_attributes: queuedItems.reduce(
          (res, item, index) => ({
            ...res,
            [index]: { id: item.line_item_id, quantity: 0 }
          }),
          {}
        )
      };

      return new Promise((resolve, reject) => {
        api.vueCart
          .updateLineItems(payload)
          .then(response => {
            clearCachedEstimates();
            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              dispatch("savedCarts/loadSavedCarts");
              resolve(response);
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            commit("shipping/setDeliveryLoadingStatus", false);
            dispatch("setSystemError", e);
            reject(e);
          });
      });
    },

    async processRemoveItemQueue({ dispatch, commit, getters }) {
      if (!getters.hasQueuedRemovals) {
        commit("setRemoveItemProcessing", false);
        return;
      }

      const queuedItems = Object.values(getters.removeItemQueue);
      if (queuedItems.length === 0) {
        commit("setRemoveItemProcessing", false);
        return;
      }

      try {
        await dispatch("processRemoveItems", queuedItems);
        // Remove the processed items from the queue only after successful processing
        queuedItems.forEach(item => {
          commit("removeFromQueue", item);
        });
      } catch (error) {
        dispatch("setSystemError", error);
        // Remove failed items from queue to prevent infinite retry
        queuedItems.forEach(item => {
          commit("removeFromQueue", item);
        });
      }

      // Recursively process any remaining queue items that were added during batch processing
      dispatch("processRemoveItemQueue");
    },

    clearOosItems({ getters, dispatch }) {
      const oos = getters.itemsOutOfStock;
      const payload = {
        line_items_attributes: Object.keys(oos).reduce(
          (res, key, index) => ({
            ...res,
            [index]: { id: key, quantity: oos[key].onhand }
          }),
          {}
        )
      };

      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .updateLineItems(payload)
          .then(response => {
            clearCachedEstimates();
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          });
      });
    },

    updateLineItemQuantity({ dispatch, commit }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        const requestStartTime = performance?.now() || 0;
        api.vueCart
          .updateLineItem(payload)
          .then(response => {
            clearCachedEstimates();
            const requestEndTime = performance?.now() || 0;
            const requestTotalTime = requestEndTime - requestStartTime;
            commit("shipping/setChangeDeliveryDateMixpanelData", {
              earliestTimeToGetDate: requestTotalTime,
              cartTimeToGetDate: requestTotalTime
            });
            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              dispatch("savedCarts/loadSavedCarts");
              resolve();
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("shipping/setDeliveryLoadingStatus", false);
          });
      });
    },

    async addToCart({ dispatch }, formData) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.cart
          .madeAdd(formData)
          .then(response => {
            dispatch("refreshCartState");
            resolve(response);
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject(e);
            window.Honeybadger?.notify("Failed to add to cart", {
              params: {
                error: JSON.stringify(e),
                formData: JSON.stringify(formData)
              }
            });
          });
      });
    },

    async updateUseStoreCredits({ commit, dispatch }, value) {
      commit("updateStoreCreditUsageState", value);
      commit("setCheckoutLoadingStatus", true);
      commit("setInventoryLoadingStatus", true);

      try {
        const response = await api.vueCart.setUseStoreCredits({ use_store_credits: value });
        dispatch("replaceCartState", response);
      } catch (error) {
        window.Honeybadger?.notify("Failed to update store credits usage state", {
          params: {
            error: JSON.stringify(error),
            use_store_credits: value
          }
        });
      } finally {
        commit("setCheckoutLoadingStatus", false);
      }
    },
    async updateUseSpecificStoreCredits({ commit, dispatch }, { businessLine, isUsed }) {
      commit("updateSpecificStoreCreditUsageState", { businessLine, isUsed });
      commit("setCheckoutLoadingStatus", true);
      commit("setInventoryLoadingStatus", true);

      const underscoredBusinessLine = businessLine
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .toLowerCase();
      try {
        const response = await api.vueCart.setUseStoreCredits({
          business_line: underscoredBusinessLine,
          use_store_credits: isUsed
        });
        dispatch("replaceCartState", response);
      } catch (error) {
        window.Honeybadger?.notify("Failed to update store credits usage state", {
          params: {
            error: JSON.stringify(error),
            business_line: businessLine,
            use_store_credits: isUsed
          }
        });
      } finally {
        commit("setCheckoutLoadingStatus", false);
      }
    },

    async getLocation({ state, dispatch }, zipcode) {
      try {
        const data = {
          order_number: state.cartState.cartConfig.orderNumber,
          embroidery_test_order: false,
          zipcode,
          log_not_existing_zipcode: true
        };
        const payload = await api.locations.create(data);

        // Catch error from server
        if (payload.error) throw new PlainError(payload.message);

        if (payload.cities) {
          const [selectedCity] = payload.cities;

          dispatch("replaceCartState", {
            notifications: [],
            locationAddresses: payload.cities,
            location: {
              city: selectedCity.name,
              stateCode: selectedCity.state_code,
              zipcode: payload.zipcode,
              citySelected: true
            }
          });

          dispatch("putLocation", selectedCity);

          track("location_updated", {
            location: `${selectedCity.name}, ${selectedCity.state_code}, ${payload.zipcode}`
          });
        }

        // fill related places with selected address
        if (payload.session) {
          window.JiffyDeliverTo.updateZipCodeInHeader(payload.session);
        }

        return payload;
      } catch (e) {
        dispatch("setSystemError", e);
        return null;
      }
    },

    fetchVariantsData({ commit }, lineItem) {
      return new Promise((resolve, reject) => {
        api.vueCart
          .getCartVariants({
            id: lineItem.compositionId,
            count: lineItem.printSizes?.length || 1
          })
          .then(({ success, variants }) => {
            if (!success) {
              reject(new Error("Error fetching variants data. Please try again."));
              return;
            }
            commit("setVariantsData", variants);
            resolve(variants);
          })
          .catch(_error => {
            reject(new Error("Error fetching variants data. Please try again."));
          });
      });
    },

    fetchMatchingItems(_, lineItemId) {
      return new Promise((resolve, reject) => {
        api.cart
          .matchingVariants({ lineItemId })
          .then(response => {
            resolve(objNamesToCamelCase(response));
          })
          .catch(_error => {
            reject(new Error("Error fetching matching items. Please try again."));
          });
      });
    },

    fetchProductData({ commit }, lineItem) {
      return new Promise((resolve, reject) => {
        api.printOnDemand
          .getProduct({
            catalog_number: lineItem.productStyle
          })
          .then(response => {
            commit("setProductData", objNamesToCamelCase(response));
            resolve(response);
          })
          .catch(_error => {
            reject(new Error("Error fetching product data. Please try again."));
          });
      });
    },

    async updateVariantQuantities({ commit, dispatch, getters }, data) {
      const defaultErrorMessage = "Error updating variant quantities. Please try again.";
      dispatch("prepareToAjax");

      const reportError = (errorMessage, errorStatus, errorType) => {
        if (!errorStatus) {
          commit("setCouponLoadingStatus", false);
          commit("shipping/setDeliveryLoadingStatus", false);
          return;
        }

        const params = {
          error_message: errorMessage,
          error_status: errorStatus,
          error_type: errorType,
          order_number: getters.orderNumber,
          payload: data
        };
        window.Honeybadger?.notify("Failed to update variant quantities", { params });
        track("cart_edit_size_update_error", params);
      };

      try {
        return await api.vueCart.updateVariantQuantities(data);
      } catch (error) {
        let errorParsed;
        try {
          errorParsed = JSON.parse(error.message);
        } catch {
          errorParsed = { error: error.message };
        }
        const errorMessage = errorParsed.error || defaultErrorMessage;
        reportError(
          errorMessage,
          errorParsed?.mixpanel_data?.code || error.status,
          errorParsed?.mixpanel_data?.type
        );
        throw new Error(errorMessage);
      }
    },

    async putLocation({ state, commit, dispatch }, city) {
      try {
        commit("setLocationLoadingStatus", true);
        const { location } = state.cartState;
        const data = {
          zipcode: location.zipcode,
          city_name: {
            name: city.name,
            state_code: city.stateCode
          }
        };
        await api.locations.update({ id: location.zipcode }, data);
        dispatch("getCurrentLocation");
        /**
         * TODO: Investigate the bug. Instead of location.city should be cityName, but cityName doesn't saved on BE.
         *       After reloading the page will be shown the first city from the initial list
         */
        window.JiffyDeliverTo.updateZipCodeInHeader(location);
      } catch (e) {
        dispatch("setSystemError", e);
        commit("setLocationLoadingStatus", false);
      }
    },

    async getCurrentLocation({ commit, dispatch }) {
      try {
        commit("setLocationLoadingStatus", true);
        const cacheBuster = new Date().getTime();
        const response = await api.locations.getCurrentLocation({ cb: cacheBuster });
        const location = objNamesToCamelCase(response);
        commit("getCurrentLocationSuccess", location);

        if (location.citySelected || location.cityDefault) {
          dispatch("shipping/getAllDeliveryDates", null, { root: true });
          commit("setGuestZipcode", location.zipcode);
          registerMixpanel({ zipcode: location.zipcode });
        }
      } catch (e) {
        dispatch("setSystemError", e);
      } finally {
        commit("setLocationLoadingStatus", false);
      }
    },

    changeAddress({ dispatch, commit, state }, { id }) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .setShipAddress({ ship_address_id: id })
          .then(response => {
            commit("changeAddressSuccess", id);
            dispatch("setCartResponse", response);

            const selectedAddress = state.cartState.accountAddresses.addresses.find(
              address => address.id === id
            );

            if (selectedAddress) {
              window.JiffyDeliverTo.updateUserAddressInHeader(selectedAddress.id);
              registerMixpanel({ zipcode: selectedAddress.zipcode });
            }

            resolve();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          });
      });
    },
    updatePreCut({ commit, _getters, dispatch }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .preCut(payload)
          .then(response => {
            if (response.success) {
              dispatch("refreshCartState");
              dispatch("shipping/getAllDeliveryDates");
            }
            resolve(response);
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("shipping/setDeliveryLoadingStatus", false);
          });
      });
    },
    updateArtworkSize({ commit, dispatch }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .updateDtfSize(payload)
          .then(response => {
            if (response.success) {
              dispatch("refreshCartState");
            }
            resolve(response);
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("shipping/setDeliveryLoadingStatus", false);
          });
      });
    },
    addArtworkSize({ commit, dispatch }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .addDtfSize(payload)
          .then(response => {
            if (response.success) {
              dispatch("refreshCartState");
              commit("setAddedArtworkSize", response.new_line_item_ids[0]);
            }
            resolve(response);
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("shipping/setDeliveryLoadingStatus", false);
          });
      });
    },
    setCoupon({ dispatch }, couponCode) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .setCouponCode({ coupon_code: couponCode })
          .then(response => {
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          });
      });
    },

    removeCoupon({ dispatch }, reason = "") {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .removeCouponCode({ reason })
          .then(response => {
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          });
      });
    },

    refreshCartState({ commit, dispatch }) {
      dispatch("prepareToAjax");
      commit("setRefreshLoading", true);
      commit("setCheckoutLoadingStatus", false);

      const cacheBuster = new Date().getTime();
      const requestStartTime = performance?.now() || 0;
      return new Promise((resolve, reject) => {
        api.vueCart
          .getCartState({ cb: cacheBuster })
          .then(response => {
            clearCachedEstimates();
            const requestEndTime = performance?.now() || 0;
            const requestTotalTime = requestEndTime - requestStartTime;
            commit("shipping/setChangeDeliveryDateMixpanelData", {
              earliestTimeToGetDate: requestTotalTime,
              cartTimeToGetDate: requestTotalTime
            });

            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              resolve();
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            dispatch("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("setRefreshLoading", false);
          });
      });
    },

    checkCartItems({ commit, getters, state }) {
      // Define the function before using it
      function lineItemsChanged(latestLineItems, pageLineItems) {
        if (Object.keys(latestLineItems).length === 0) {
          return true;
        }

        const combinedLineItems = Object.values(pageLineItems).flatMap(lineItemGroup =>
          lineItemGroup.lineItems ? Object.values(lineItemGroup.lineItems) : []
        );

        const pageLineItemsMap = combinedLineItems.reduce((map, item) => {
          map[item.id] = item.quantity;

          const groupedItems = item.assemblyGroupedItems?.sizeAndQuantity ?? [];

          if (groupedItems.length) {
            groupedItems.forEach(groupedItem => {
              map[groupedItem.lineItemId] = groupedItem.quantity;
            });
          }

          return map;
        }, {});

        return Object.keys(latestLineItems).some(
          id => pageLineItemsMap[id] !== latestLineItems[id]
        );
      }

      // Now use the function
      api.vueCart.currentItems().then(response => {
        if (lineItemsChanged(response.items, state.cartState.orderGroups.groups)) {
          scrollToTop();
          track("multitab_browsing", {
            order_number: getters.orderNumber,
            item_count: state.cartState.summary.totalItems
          });
          commit("pushNotification", {
            type: "warning",
            text: "Your cart items are changed. Updating your cart..."
          });

          window.location.reload();
        }
      });
    },
    async obtainEtsyLineItemsByListing({ commit, getters }) {
      api.merchantListings
        .unmappedLineItems({ order_number: getters.orderNumber })
        .then(response => {
          if (response.success) {
            commit("setEtsyObservationPeriod", response.period);
            commit("setPlatformName", response.platform_name);
            commit("setUnmappedEtsyLineItemsByListing", response.listings);
            return;
          }

          commit("setUnmappedEtsyLineItemsByListing", []);
        })
        .catch(() => {
          commit("setUnmappedEtsyLineItemsByListing", []);
        });
    },
    hardRefresh({ commit, dispatch }) {
      dispatch("prepareToAjax");
      commit("setRefreshLoading", true);
      window.location.reload();
    },

    async setEnhancementPreferences(_, { id, requestNote, dtfPrintId, callback, event }) {
      try {
        callback("saving");
        const apiParams = {
          dtf_artwork: {
            enhancement_request_note: requestNote,
            enhance_image: true
          }
        };
        await api.dtf.updateArtworkRedrawData({ id }, apiParams);

        const dtfPrintApiParams = {
          dtf_print: {
            enhancement_request_note: requestNote,
            enhance_image: true
          }
        };
        await api.dtfPrint.updateRedrawData({ id: dtfPrintId }, dtfPrintApiParams);

        submitDtfEvent("designer_notes_cart", {
          artwork_id: id,
          note: requestNote,
          button_pressed: event
        });
        callback("view");
      } catch (error) {
        window.Honeybadger?.notify("Artwork enhancement setting save from cart failed", {
          params: { error_message: error.message }
        });
      }
    },

    async createLlmRedrawRequest({ commit, dispatch }, { artworkId, initialPrompt, dtfPrintId }) {
      commit("setLlmRedrawLoading", { artworkId, isLoading: true });

      // Ensure minimum loading time for better UX
      const startTime = Date.now();
      const minimumLoadingTime = 800; // milliseconds

      try {
        const requestData = {
          llm_redraw_request: {
            dtf_artwork_id: artworkId,
            initial_prompt: initialPrompt
          }
        };

        // Add dtf_print_id at the root level if available
        if (dtfPrintId) {
          requestData.dtf_print_id = dtfPrintId;
        }

        const response = await api.llmRedrawRequests.create(requestData);

        const llmRedrawRequestId =
          response.id ||
          response.data?.id ||
          response.llm_redraw_request?.id ||
          response.llm_redraw_request_id;

        const clarifyingQuestions =
          response.clarifying_questions || response.data?.clarifying_questions || [];

        // Store in state
        commit("setLlmRedrawRequest", {
          artworkId,
          data: {
            llmRedrawRequestId,
            initialPrompt,
            questions: clarifyingQuestions.map(q => ({
              title: q.question || q.text || q.title,
              options: q.options || [],
              answer: null
            })),
            currentState: clarifyingQuestions.length > 0 ? "questions" : "final"
          }
        });

        return { llmRedrawRequestId, clarifyingQuestions };
      } catch (error) {
        dispatch("setSystemError", error.message || "Failed to process your instructions");
        throw error;
      } finally {
        // Ensure minimum loading time has elapsed
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minimumLoadingTime - elapsedTime;

        if (remainingTime > 0) {
          await new Promise(resolve => {
            setTimeout(resolve, remainingTime);
          });
        }

        commit("setLlmRedrawLoading", { artworkId, isLoading: false });
      }
    },

    updateLlmRedrawRequest(
      { commit, dispatch },
      { artworkId, llmRedrawRequestId, followUpData, dtfPrintId }
    ) {
      commit("setLlmRedrawLoading", { artworkId, isLoading: true });

      // Ensure minimum loading time for better UX
      const startTime = Date.now();
      const minimumLoadingTime = 800; // milliseconds

      const updateData = {
        llm_redraw_request: {
          follow_up_questions_and_responses: followUpData
        }
      };

      // Add dtf_print_id at the root level if available
      if (dtfPrintId) {
        updateData.dtf_print_id = dtfPrintId;
      }

      return api.llmRedrawRequests
        .update({ id: llmRedrawRequestId }, updateData)
        .then(() => {
          // Update state to final after successful submission
          commit("updateLlmRedrawRequestState", { artworkId, currentState: "final" });
        })
        .catch(error => {
          dispatch("setSystemError", error.message || "Failed to save your answers");
          throw error;
        })
        .finally(async () => {
          // Ensure minimum loading time has elapsed
          const elapsedTime = Date.now() - startTime;
          const remainingTime = minimumLoadingTime - elapsedTime;

          if (remainingTime > 0) {
            await new Promise(resolve => {
              setTimeout(resolve, remainingTime);
            });
          }

          commit("setLlmRedrawLoading", { artworkId, isLoading: false });
        });
    },

    updateLlmRedrawQuestionAnswer({ commit, state }, { artworkId, questionIndex, answer }) {
      const questions = [
        ...(state.cartState.llmRedrawRequests.byArtworkId[artworkId]?.questions || [])
      ];
      if (questions[questionIndex]) {
        questions[questionIndex].answer = answer;
        commit("setLlmRedrawQuestions", { artworkId, questions });
      }
    },

    resetLlmRedrawRequest({ commit }, artworkId) {
      commit("clearLlmRedrawRequest", artworkId);
    },

    async fetchMatchingProduct({ commit, state }, payload) {
      try {
        const data = await api.vueCart.fetchMatchingProduct({
          catalog_number: payload.catalogId,
          composition_id: payload.compositionId,
          line_item_id: state.cartState.browseCustomShirtsLineItemId
        });
        const lineItem = objNamesToCamelCase(data);
        commit("setMatchingProduct", lineItem);
        return lineItem;
      } catch (error) {
        console.error("Error fetching matching product:", error);
        throw error;
      }
    },

    async handleBrowseCustomShirts({ commit }, lineItemId) {
      // Store the line item ID in the store state for later use
      commit("setBrowseCustomShirtsLineItemId", lineItemId);

      // Return the line item ID for the component to handle modal opening
      return lineItemId;
    },

    initializeLlmRedrawRequest({ commit }, { artworkId, enhancementNote, llmRedrawRequestId }) {
      // Business logic for initialization
      if (enhancementNote) {
        // Initialize with existing enhancement note
        commit("setLlmRedrawRequest", {
          artworkId,
          data: {
            llmRedrawRequestId: llmRedrawRequestId || null,
            initialPrompt: enhancementNote,
            questions: [],
            currentState: "final"
          }
        });
      } else {
        // Initialize empty state
        commit("setLlmRedrawRequest", {
          artworkId,
          data: {
            llmRedrawRequestId: null,
            initialPrompt: "",
            questions: [],
            currentState: "initial"
          }
        });
      }
    },

    setSystemError({ commit, dispatch }, error) {
      let message;

      if (error instanceof Error) {
        message = error.message;

        if (error.status === 500) {
          dispatch("shipping/getAllDeliveryDates", null, { root: true });
        }
      } else {
        message = error;
      }
      commit("setSystemError", message);
    }
  },
  // plugins
  plugins,
  modules: {
    dtfBogo: dtfBogoModule,
    savedCarts: savedCartsModule,
    shipping: shippingModule,
    payments: paymentsModule
  }
});

// Register EventBus listener to dispatch proper Vuex action
EventBus.on(Events.SAVED_CARTS_LOADED, (_, data) => {
  store.dispatch("savedCarts/updateFromEventBus", data);
});

export default store;
